import { useState } from 'react';
import { atHomeFileFinding, type ReimbursementState } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { OpCtx } from './OperatorHome';
import { Act, chip16, D, S } from './ui';
import { BuildLabel, useCustomerText } from '../../ui/buildLabels';

// Reimbursements (design T12175-12232, hOpRebate): every finding names the reading that
// proves it. Reads the PROPOSED GET /athome/reimbursements; "File it" is the PROPOSED
// POST /athome/reimbursements/:id/file.

const SC: Record<ReimbursementState, string> = { READY: '#1F6B52', FILED: '#2F5C97', IGNORED: '#4A5866', 'NEEDS A READING': '#54606C' };
const ORDER: ReimbursementState[] = ['READY', 'IGNORED', 'NEEDS A READING', 'FILED'];
const HEAD = 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#22272E;';
const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

export function OpReimbursements({ c }: { c: OpCtx }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const r = c.o.rebate.data;
  const [err, setErr] = useState<string | null>(null);
  if (!r) {
    return (
      <D css="padding-top:8px;font-size:16px;color:#54606C;">
        {c.o.rebate.error ? plain('Reimbursements are not connected yet: waiting on GET /athome/reimbursements. No claim is shown without the reading that proves it.') : ''}
      </D>
    );
  }
  const rows = r.findings;
  const by = (s: ReimbursementState) => rows.filter((x) => x.state === s);
  const sum = (a: typeof rows) => usd(a.reduce((t, x) => t + x.amountUsd, 0));
  const file = async (id: string) => {
    if (c.readOnly) return;
    setErr(null);
    try {
      await atHomeFileFinding(client, { params: { id } });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      c.o.rebate.reload();
    }
  };
  return (
    <D css="display:flex;flex-direction:column;gap:9px;min-width:0;">
      <D css="display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;row-gap:6px;min-width:0;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);">
        <S css="flex:0 0 auto;font-size:21px;font-weight:700;letter-spacing:-.025em;line-height:1.15;color:#12503C;">{`${sum(by('READY'))} ready to claim across ${r.members} members`}</S>
        <S css="flex:1 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;">{`${rows.length} findings from the engine · ${sum(by('IGNORED'))} being left on the table · ${sum(by('NEEDS A READING'))} blocked on a missing reading.${r.real ? '' : ' Nothing files until the console is wired to the claims service.'}`}</S>
        <BuildLabel><S css={chip16('#54606C', 'rgba(34,39,46,.09)')}>MODELED</S></BuildLabel>
      </D>
      <D css="display:flex;align-items:flex-start;gap:24px;flex-wrap:wrap;row-gap:8px;min-width:0;">
        {([['Ready', 'READY'], ['Left on the table', 'IGNORED'], ['Blocked on a reading', 'NEEDS A READING'], ['Filed here', 'FILED']] as const).map(([k, s]) => (
          <S key={k} css="flex:0 1 auto;min-width:0;display:block;">
            <S css="display:block;font-size:14px;letter-spacing:.1em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;">{k}</S>
            <S css={'display:block;font-size:24px;font-weight:700;letter-spacing:-.03em;line-height:1.1;font-variant-numeric:tabular-nums;color:' + (s === 'IGNORED' ? '#4A5866' : SC[s]) + ';'}>{sum(by(s))}</S>
            <S css="display:block;font-size:14px;color:#54606C;">{`${by(s).length} findings`}</S>
          </S>
        ))}
      </D>
      <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
          <S css={HEAD}>By code</S>
          <S css="flex:1 1 160px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">how much of each code we are capturing, and the dollars in what we are not</S>
        </D>
        <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:2px 24px;min-width:0;">
          {r.codes.map((code, i) => {
            const mine = rows.filter((x) => x.code === code.code);
            const got = mine.filter((x) => x.state === 'READY' || x.state === 'FILED').length;
            const share = mine.length ? got / mine.length : 0;
            return (
              <S key={code.code} css="display:flex;align-items:center;gap:9px;padding:2px 0;min-width:0;">
                <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{`${code.code} · ${code.name}`}</S>
                <S css="flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;">{`${got} of ${mine.length}`}</S>
                <S css="flex:0 0 56px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;">
                  <S css={'display:block;height:100%;width:' + Math.round(share * 100) + '%;border-radius:3px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + i * 50 + 'ms;background:' + (share > 0.6 ? '#2F8A6F' : '#5B6B7C') + ';'} />
                </S>
                <S css="flex:0 0 52px;text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#4A5866;">{usd(mine.filter((x) => x.state === 'IGNORED' || x.state === 'NEEDS A READING').reduce((t, x) => t + x.amountUsd, 0))}</S>
              </S>
            );
          })}
        </D>
      </D>
      <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
          <S css={HEAD}>Every finding</S>
          <S css="flex:1 1 160px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">ready first, then what we are leaving, then what is blocked. Each row names the reading that proves it.</S>
        </D>
        {err && <D css="font-size:14px;color:#B3261E;" role="alert">{`Not filed: ${err}`}</D>}
        {rows
          .slice()
          .sort((a, b) => ORDER.indexOf(a.state) - ORDER.indexOf(b.state) || b.amountUsd - a.amountUsd)
          .map((x, i) => (
            <D key={x.id} testId="finding-row" css={'display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:5px 9px;border-radius:8px;min-width:0;border-left:3px solid ' + SC[x.state] + ';background:rgba(34,39,46,.035);animation:slideIn .4s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(600, i * 18) + 'ms;'}>
              <S css="flex:0 0 130px;min-width:0;font-size:16px;font-weight:700;overflow-wrap:break-word;">{x.name}</S>
              <S css="flex:0 0 auto;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#3C454F;">{x.code}</S>
              <S css="flex:1 1 170px;min-width:0;font-size:14px;color:#22272E;overflow-wrap:break-word;">{x.desc}</S>
              <S css="flex:2 1 220px;min-width:0;font-size:14px;color:#54606C;line-height:1.35;text-wrap:pretty;">{x.why}</S>
              <S css={chip16(SC[x.state], SC[x.state] + '1F')}>{x.state}</S>
              <S css="flex:0 0 46px;text-align:right;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;">{usd(x.amountUsd)}</S>
              {x.state === 'READY' && (
                <Act css="flex:0 0 auto;display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1F6B52;" onClick={() => void file(x.id)} disabled={c.readOnly} reason={c.reason}>
                  File it
                </Act>
              )}
            </D>
          ))}
      </D>
      <D css="font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;">The engine reads the same member data the console shows. A finding names the reading that proves it, so a claim is never offered without the evidence for it. Dollar figures are MODELED against the CY2026 fee schedule and are not a payment promise.</D>
    </D>
  );
}
